import { CartResponseBody } from '../../core/theme'
import { getToken } from '../../utils/get-token'
import { apiEndpointAction } from '../../utils/variables'
const HTTP_CART = apiEndpointAction('cart', 'cart')
const HTTP_CURRENCY = apiEndpointAction('current-currency', 'currency/current')
const HTTP_CURRENCIES = apiEndpointAction('currency', 'currency')
const HTTP_SWITCH_CURRENCY = apiEndpointAction(
  'switch-currency',
  'currency/switch'
)

type InferredCurrency = {
  code: string
  prefix: string
  suffix?: string
}

let activeCurrency: InferredCurrency = { code: '', prefix: '' }

const stringValue = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

type CurrencyOption = {
  id: number
  code: string
  prefix: string
  suffix?: string
}

let availableCurrencies: CurrencyOption[] = []
let latestRefreshRequestId = 0

const loadActiveCurrency = async (): Promise<InferredCurrency | undefined> => {
  try {
    const responseRAW = await fetch(HTTP_CURRENCY, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })

    if (!responseRAW.ok) {
      return
    }

    const response = (await responseRAW.json()) as {
      error?: boolean
      code?: string
      prefix?: string
      suffix?: string
    }

    if (response?.error) {
      return
    }

    return {
      code: stringValue(response?.code),
      prefix: stringValue(response?.prefix),
      suffix: stringValue(response?.suffix),
    }
  } catch {
    return
  }
}

const loadCurrencies = async (): Promise<void> => {
  try {
    const responseRAW = await fetch(HTTP_CURRENCIES, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })

    if (!responseRAW.ok) {
      return
    }

    const response = (await responseRAW.json()) as {
      error?: boolean
      currencies?: CurrencyOption[]
    }

    if (response?.error || !Array.isArray(response?.currencies)) {
      return
    }

    availableCurrencies = response.currencies
  } catch {
    return
  }
}

const renderCurrencyMenu = (): void => {
  const menu = document.querySelector<HTMLElement>('.js-currency-menu')
  if (!menu) {
    return
  }

  const items = availableCurrencies
    .filter(
      (currency) =>
        Number.isFinite(currency.id) &&
        currency.id > 0 &&
        typeof currency.code === 'string' &&
        currency.code.trim() !== ''
    )
    .map((currency) => {
      const label = currency.code.trim()
      const isActive = label === activeCurrency.code
      const item = document.createElement('li')
      const button = document.createElement('button')

      button.type = 'button'
      button.className = `dropdown-item js-currency-option${
        isActive ? ' active' : ''
      }`
      button.dataset.currencyId = String(currency.id)
      button.setAttribute('aria-pressed', String(isActive))
      button.textContent = label

      item.appendChild(button)
      return item
    })

  menu.innerHTML = ''
  items.forEach((item) => menu.appendChild(item))
}

const commitActiveCurrency = (currency: InferredCurrency): void => {
  activeCurrency = currency
  const summaryCurrency = document.querySelector<HTMLElement>(
    '.js-summary-currency'
  )
  if (summaryCurrency) {
    summaryCurrency.textContent = currency.code || '---'
  }
}

const switchCurrency = async (currencyId: number): Promise<void> => {
  if (!Number.isFinite(currencyId) || currencyId < 1) {
    return
  }

  try {
    const token = await getToken()
    const payload = new URLSearchParams({
      id: String(currencyId),
      token,
    })
    const response = await fetch(HTTP_SWITCH_CURRENCY, {
      method: 'POST',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      body: payload.toString()
    })

    if (!response.ok) {
      return
    }
  } catch {
    return
  }
}

const resolveCurrency = (cart: CartResponseBody): InferredCurrency => ({
  code: stringValue(cart.currencyCode ?? cart.currency ?? activeCurrency.code),
  prefix: stringValue(cart.currencyPrefix ?? activeCurrency.prefix),
  suffix: stringValue(cart.currencySuffix ?? activeCurrency.suffix),
})

const hasCartTotal = (cart: CartResponseBody): boolean => {
  const rawValue = cart?.total ?? cart?.cart_total ?? cart?.subtotal ?? cart?.value
  return typeof rawValue === 'string'
    ? rawValue.trim() !== ''
    : rawValue !== undefined && rawValue !== null
}

const hasCartCount = (cart: CartResponseBody): boolean => {
  if (
    nonEmpty(cart?.count) !== undefined ||
    nonEmpty(cart?.cart_count) !== undefined
  ) {
    return true
  }

  return Array.isArray(cart?.domains) || Array.isArray(cart?.products)
}

const nonEmpty = <T>(value: T | null | undefined): T | undefined => {
  if (value === null || value === undefined) {
    return undefined
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    return trimmedValue ? (trimmedValue as unknown as T) : undefined
  }

  return value
}

const getBootstrapCart = async (): Promise<CartResponseBody | undefined> => {
  const appEl = document.querySelector<HTMLElement>('#domain-dash-app')
  const bootstrapUrl = appEl?.dataset.adsBootstrapUrl
  const apiBase = appEl?.dataset.adsApiBase
  const endpoint = bootstrapUrl || (apiBase ? `${apiBase}&action=bootstrap` : '')

  if (!endpoint) {
    return
  }

  try {
    const responseRAW = await fetch(endpoint, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })

    if (!responseRAW.ok) {
      return
    }

    const response = (await responseRAW.json()) as {
      success?: boolean
      cart?: {
        count?: number
        total?: number
        subtotal?: number
      }
      cart_count?: number
      cart_total?: number
    }

    const cart = response?.cart
    return {
      count: response?.cart_count ?? cart?.count,
      total: response?.cart_total ?? cart?.total,
      subtotal: cart?.subtotal,
    }
  } catch {
    return
  }
}

const withFallbackTotal = async (cart: CartResponseBody): Promise<CartResponseBody> => {
  if (hasCartTotal(cart) && hasCartCount(cart)) {
    return cart
  }

  const bootstrapCart = await getBootstrapCart()
  if (!bootstrapCart) {
    return cart
  }

  return {
    ...cart,
    total:
      nonEmpty(cart.total) ??
      nonEmpty(cart.cart_total) ??
      nonEmpty(bootstrapCart.total) ??
      nonEmpty(bootstrapCart.cart_total),
    subtotal: nonEmpty(cart.subtotal) ?? nonEmpty(bootstrapCart.subtotal),
    count:
      nonEmpty(cart.count) ??
      nonEmpty(cart.cart_count) ??
      nonEmpty(bootstrapCart.count) ??
      nonEmpty(bootstrapCart.cart_count),
  }
}

const getNumberOfItemsInCart = (cart: CartResponseBody): number => {
  const rawReportedCount = nonEmpty(cart?.count) ?? nonEmpty(cart?.cart_count)
  const reportedCount =
    rawReportedCount === undefined ? Number.NaN : Number(rawReportedCount)
  if (Number.isFinite(reportedCount)) {
    return Math.max(0, Math.trunc(reportedCount))
  }

  let number = 0

  number = number + (cart?.domains?.length ?? 0)
  number = number + (cart?.products?.length ?? 0)

  return number
}

const parseCartTotalValue = (rawValue: unknown): number | undefined => {
  if (typeof rawValue === 'number') {
    return Number.isFinite(rawValue) ? rawValue : undefined
  }

  if (typeof rawValue !== 'string') {
    return undefined
  }

  const cleaned = rawValue.replace(/[^0-9.,-]/g, '')
  if (!cleaned) {
    return undefined
  }

  const hasComma = cleaned.includes(',')
  const hasDot = cleaned.includes('.')
  let normalized = cleaned

  if (hasComma && hasDot) {
    const decimalSeparator =
      cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.') ? ',' : '.'
    const thousandsSeparator = decimalSeparator === ',' ? '.' : ','
    normalized = cleaned
      .split(thousandsSeparator)
      .join('')
      .replace(decimalSeparator, '.')
  } else if (hasComma) {
    // Treat grouped commas as thousands separators, otherwise as decimal separator.
    if (/^-?\d{1,3}(,\d{3})+$/.test(cleaned)) {
      normalized = cleaned.split(',').join('')
    } else {
      normalized = cleaned.replace(',', '.')
    }
  } else if (hasDot) {
    if (/^-?\d{1,3}(\.\d{3})+$/.test(cleaned)) {
      normalized = cleaned.split('.').join('')
    }
  }

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : undefined
}

const getCartValueLabel = (cart: CartResponseBody): string => {
  const rawValue =
    cart?.total ?? cart?.cart_total ?? cart?.subtotal ?? cart?.value
  const currency = resolveCurrency(cart)
  if (rawValue === undefined || rawValue === null || rawValue === '') {
    return ''
  }

  const value = parseCartTotalValue(rawValue)
  if (value === undefined) {
    return ''
  }

  const currencyCode = currency.code
  const suffix = currency.suffix
    ? ` ${currency.suffix}`
    : !currency.prefix && currencyCode
      ? ` ${currencyCode}`
      : ''
  const separator = currency.prefix && value >= 0 ? ' ' : ''

  return `${currency.prefix}${separator}${value.toFixed(2)}${suffix}`.trim()
}

const updateCartButtons = (
  buttons: NodeListOf<HTMLAnchorElement>,
  cart: CartResponseBody
): void => {
  const itemCount = getNumberOfItemsInCart(cart).toString()
  const cartValueLabel = getCartValueLabel(cart)
  const currency = resolveCurrency(cart)
  const summaryCurrencyEL = document.querySelector<HTMLElement>(
    '.js-summary-currency'
  )

  buttons.forEach((button) => {
    const itemsInCartEL = button.querySelector('.js-number')
    if (!itemsInCartEL) {
      throw new Error('Cart: number holder is missing')
    }
    itemsInCartEL.textContent = itemCount

    const cartCountStatusEL = button.querySelector<HTMLElement>(
      '.js-cart-count-status'
    )
    if (cartCountStatusEL) {
      cartCountStatusEL.textContent = `Cart contains ${itemCount} ${Number(itemCount) === 1 ? 'item' : 'items'}`
    }

    const hoverCountEL = button.querySelector('.js-hover-count')
    if (hoverCountEL) {
      hoverCountEL.textContent = itemCount
    }

    const hoverNounEL = button.querySelector('.js-hover-noun')
    if (hoverNounEL) {
      hoverNounEL.textContent = Number(itemCount) === 1 ? 'item' : 'items'
    }

    const cartValueEL = button.querySelector('.js-value')
    if (cartValueEL) {
      if (cartValueLabel) {
        cartValueEL.textContent = cartValueLabel
        cartValueEL.removeAttribute('hidden')
      } else {
        cartValueEL.textContent = ''
        cartValueEL.setAttribute('hidden', '')
      }
    }

    const hoverValueEL = button.querySelector('.js-hover-value')
    if (hoverValueEL) {
      if (cartValueLabel) {
        hoverValueEL.textContent = cartValueLabel
        hoverValueEL.removeAttribute('hidden')
      } else {
        hoverValueEL.textContent = ''
        hoverValueEL.setAttribute('hidden', '')
      }
    }

    const currencyEL = button.querySelector('.js-hover-currency')
    if (currencyEL) {
      currencyEL.textContent = currency.code
    }

    const itemText = `${itemCount} ${Number(itemCount) === 1 ? 'item' : 'items'}`
    const valueText = cartValueLabel ? `, ${cartValueLabel}` : ''
    button.setAttribute('aria-label', `Cart, ${itemText}${valueText}`)

    if (summaryCurrencyEL && button === buttons[0]) {
      summaryCurrencyEL.textContent = currency.code || '---'
      summaryCurrencyEL.hidden = false
    }
  })
}

const flashTimers = new WeakMap<HTMLAnchorElement, number>()

const flashCartButtons = (buttons: NodeListOf<HTMLAnchorElement>): void => {
  const flashDurationMs = 1500
  buttons.forEach((button) => {
    const previousTimer = flashTimers.get(button)
    if (previousTimer !== undefined) {
      window.clearTimeout(previousTimer)
    }
    button.classList.remove('cart--flash')
    button.classList.add('cart--show-panel')
    void button.offsetWidth
    button.classList.add('cart--flash')
    const timer = window.setTimeout(() => {
      button.classList.remove('cart--flash', 'cart--show-panel')
      flashTimers.delete(button)
    }, flashDurationMs)
    flashTimers.set(button, timer)
  })
}

const getCartContents = async (): Promise<CartResponseBody | undefined> => {
  try {
    const responseRAW = await fetch(HTTP_CART, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include'
    })

    if (!responseRAW.ok) {
      return
    }

    const contentType = responseRAW.headers.get('content-type') ?? ''
    if (!contentType.toLowerCase().includes('application/json')) {
      return
    }

    const response = (await responseRAW.json()) as CartResponseBody
    return response && typeof response === 'object' ? response : {}
  } catch {
    return
  }
}

export const refreshCart = async (
  shouldRefreshCurrency = true
): Promise<void> => {
  const requestId = ++latestRefreshRequestId
  if (shouldRefreshCurrency) {
    const currency = await loadActiveCurrency()
    if (requestId !== latestRefreshRequestId) {
      return
    }
    if (currency) {
      commitActiveCurrency(currency)
    }
  }
  const cartContents = await getCartContents()
  if (!cartContents) {
    return
  }
  const cart = await withFallbackTotal(cartContents)
  if (requestId !== latestRefreshRequestId) {
    return
  }
  window.dispatchEvent(
    new CustomEvent<CartResponseBody>('cartContentsUpdated', { detail: cart })
  )
}

export const setupCart = async (
  buttons: NodeListOf<HTMLAnchorElement>
): Promise<void> => {
  let currentCart: CartResponseBody = {}
  let hasHydratedCart = false
  let currencySwitchQueue: Promise<void> = Promise.resolve()
  let initialCurrencyRequestActive = true

  const activeCurrencyPromise = loadActiveCurrency().then((currency) => {
    if (initialCurrencyRequestActive && currency) {
      commitActiveCurrency(currency)
    }
  })
  const currencyListPromise = loadCurrencies()
  const initialCurrencyPromise = Promise.all([
    activeCurrencyPromise,
    currencyListPromise,
  ]).then(() => {
    renderCurrencyMenu()
  })
  renderCurrencyMenu()
  updateCartButtons(buttons, currentCart)

  const resolveEventCartDetail = (
    detail: CartResponseBody & { cart?: CartResponseBody }
  ): CartResponseBody => {
    if (detail?.cart && typeof detail.cart === 'object') {
      return {
        ...detail,
        ...detail.cart,
        count:
          nonEmpty(detail.count) ??
          nonEmpty(detail.cart_count) ??
          nonEmpty(detail.cart.count) ??
          nonEmpty(detail.cart.cart_count),
        total:
          nonEmpty(detail.total) ??
          nonEmpty(detail.cart_total) ??
          nonEmpty(detail.cart.total) ??
          nonEmpty(detail.cart.cart_total),
      }
    }

    return detail
  }

  const handleCartUpdate = (event: Event): void => {
    const detail = resolveEventCartDetail(
      ((event as CustomEvent<CartResponseBody>).detail ?? {}) as CartResponseBody & {
        cart?: CartResponseBody
      }
    )
    if (!detail) {
      return
    }

    const isFullCartUpdate = event.type === 'cartContentsUpdated'
    const detailCount =
      nonEmpty(detail.count) ?? nonEmpty(detail.cart_count)
    const detailTotal =
      nonEmpty(detail.total) ??
      nonEmpty(detail.cart_total) ??
      nonEmpty(detail.subtotal) ??
      nonEmpty(detail.value)
    const hasDetailedItems =
      Array.isArray(detail.domains) || Array.isArray(detail.products)

    currentCart = {
      ...(isFullCartUpdate ? {} : currentCart),
      ...detail,
      count: isFullCartUpdate
        ? detailCount
        : detailCount ?? (hasDetailedItems ? undefined : currentCart.count),
      cart_count: isFullCartUpdate
        ? nonEmpty(detail.cart_count)
        : hasDetailedItems
          ? undefined
          : currentCart.cart_count,
      total: isFullCartUpdate ? detailTotal : detailTotal ?? currentCart.total,
      cart_total: isFullCartUpdate ? undefined : currentCart.cart_total,
      subtotal: isFullCartUpdate ? undefined : currentCart.subtotal,
      value: isFullCartUpdate ? undefined : currentCart.value,
    }
    updateCartButtons(buttons, currentCart)

    if (hasHydratedCart) {
      flashCartButtons(buttons)
      return
    }

    hasHydratedCart = true
  }

  window.addEventListener('advancedDomainSearchCartUpdated', handleCartUpdate)
  window.addEventListener('cartContentsUpdated', handleCartUpdate)
  window.addEventListener('cartRefreshRequested', () => {
    void refreshCart()
  })

  void initialCurrencyPromise

  document.addEventListener('click', (event) => {
    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    const currencyOption = target.closest<HTMLElement>('.js-currency-option')
    if (currencyOption) {
      initialCurrencyRequestActive = false
      const currencyId = Number(currencyOption.dataset.currencyId)
      currencySwitchQueue = currencySwitchQueue.then(async () => {
        await switchCurrency(currencyId)
        const currency = await loadActiveCurrency()
        if (currency) {
          commitActiveCurrency(currency)
        }
        renderCurrencyMenu()
        await refreshCart(false)
      })
      return
    }

    const trigger = target.closest(
      'a[href*="currency="], button[data-currency], [data-currency-code]'
    )
    if (!trigger) {
      return
    }

    void (async () => {
      initialCurrencyRequestActive = false
      const currency = await loadActiveCurrency()
      if (currency) {
        commitActiveCurrency(currency)
      }
      renderCurrencyMenu()
      await refreshCart(false)
    })()
  })

  await activeCurrencyPromise
  await refreshCart(false)
}
